import React, { useEffect, useMemo, useState } from 'react'
import { Button } from 'react-bootstrap'
import apiDefinitions from '../../../../core/api/apiDefinitions'

/** Types */
type ScheduleType = '' | 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'YEARLY' | 'CUSTOM' | null
type ItemStatus = 'ACTIVE' | 'INACTIVE' | 'COMPLETED' | string

interface Item {
  id: number
  itemName: string
  brandId: number | null
  description: string | null
  capacity: string | null
  itemTypeId: number | null
  buildingId: number | null
  floorId: number | null
  roomId: number | null

  /** service fields (varies across APIs) */
  serviceRequired?: boolean | null
  isServiceRequired?: boolean | null
  scheduleType?: ScheduleType
  startDate?: string | null
  endDate?: string | null
  customDays?: number | null
  intervalMonths?: number | null

  /** NEW lifecycle/status fields */
  serialNumber?: string | null
  purchaseDate?: string | null       // ISO
  usingSinceDate?: string | null     // ISO
  warrantyEndDate?: string | null    // ISO
  status?: ItemStatus | null
  comment?: string | null
}

interface ItemBrand { id: number; brandName: string; description: string }
interface ItemType { id: number; itemType: string; description: string }
interface BuildingDTO { id: number; name: string; noOfFloors: number; locationName: string; locationId: number; description?: string | null }
interface FloorDTO { id: number; name: string; floorNumber: number; buildingId: number; buildingName: string; locationName: string; createdBy: string; createdAt: string; updatedBy?: string; updatedAt?: string }
interface RoomDTO { id: number; name: string; floorId: number; buildingId: number; status: string; createdBy: string; createdAt: string; updatedBy?: string; updatedAt?: string; description?: string }

interface ViewItemModuleProps {
  show: boolean
  onHide: () => void
  item: Item | null
  itemBrands: ItemBrand[]
  itemTypes: ItemType[]
  buildings: BuildingDTO[]
  floors: FloorDTO[]
  rooms: RoomDTO[]
}

/** Helpers */
const formatDate = (iso?: string | null): string => {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  // render as YYYY-MM-DD
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

const statusBadgeClass = (s?: ItemStatus | null): string => {
  switch ((s || '').toUpperCase()) {
    case 'ACTIVE': return 'badge-soft-success'
    case 'INACTIVE': return 'badge-soft-secondary'
    case 'COMPLETED': return 'badge-soft-primary'
    default: return 'badge-soft'
  }
}

const ViewItemModule: React.FC<ViewItemModuleProps> = ({
  show,
  onHide,
  item,
  itemBrands,
  itemTypes,
  buildings,
  floors,
  rooms
}) => {
  /** Build quick lookup maps from props */
  const buildingsById = useMemo(() => {
    const m = new Map<number, BuildingDTO>()
    buildings.forEach(b => m.set(b.id, b))
    return m
  }, [buildings])

  const floorsById = useMemo(() => {
    const m = new Map<number, FloorDTO>()
    floors.forEach(f => m.set(f.id, f))
    return m
  }, [floors])

  const roomsById = useMemo(() => {
    const m = new Map<number, RoomDTO>()
    rooms.forEach(r => m.set(r.id, r))
    return m
  }, [rooms])

  /** Local fallbacks (when not in props lists) */
  const [fallbackBuilding, setFallbackBuilding] = useState<BuildingDTO | null>(null)
  const [fallbackFloor, setFallbackFloor] = useState<FloorDTO | null>(null)
  const [fallbackRoom, setFallbackRoom] = useState<RoomDTO | null>(null)
  const [loadingFields, setLoadingFields] = useState<{building?: boolean; floor?: boolean; room?: boolean}>({})

  /** Resolve brand/type */
  const brandName = useMemo(
    () => (item?.brandId ? (itemBrands.find(b => b.id === item.brandId)?.brandName ?? 'N/A') : 'N/A'),
    [item, itemBrands]
  )
  const itemTypeName = useMemo(
    () => (item?.itemTypeId ? (itemTypes.find(t => t.id === item.itemTypeId)?.itemType ?? 'N/A') : 'N/A'),
    [item, itemTypes]
  )

  /** Try to resolve room/floor/building from props first */
  const propRoom = item?.roomId ? roomsById.get(item.roomId) : undefined
  const propFloor = item?.floorId ? floorsById.get(item.floorId) : undefined
  const propBuildingDirect = item?.buildingId ? buildingsById.get(item.buildingId) : undefined
  const propBuildingFromFloor = propFloor?.buildingId ? buildingsById.get(propFloor.buildingId) : undefined
  const propBuildingFromRoom = propRoom?.buildingId ? buildingsById.get(propRoom.buildingId) : undefined

  /** API-hydrate any missing pieces when modal opens or item changes */
  useEffect(() => {
    if (!show || !item) return

    const run = async () => {
      // Room
      if (item.roomId && !propRoom && !fallbackRoom) {
        setLoadingFields(s => ({ ...s, room: true }))
        try {
          const res = await apiDefinitions.getRoomById(item.roomId)
          if (res?.status === 200 && res.data?.data) setFallbackRoom(res.data.data as RoomDTO)
        } finally {
          setLoadingFields(s => ({ ...s, room: false }))
        }
      } else if (!item.roomId) {
        setFallbackRoom(null)
      }

      // Floor (direct or via room)
      const needFloorId = item.floorId ?? fallbackRoom?.floorId ?? propRoom?.floorId ?? null
      if (needFloorId && !propFloor && !fallbackFloor) {
        setLoadingFields(s => ({ ...s, floor: true }))
        try {
          const res = await apiDefinitions.getFloorById(needFloorId)
          if (res?.status === 200 && res.data?.data) setFallbackFloor(res.data.data as FloorDTO)
        } finally {
          setLoadingFields(s => ({ ...s, floor: false }))
        }
      } else if (!needFloorId) {
        setFallbackFloor(null)
      }

      // Building (direct or via floor/room)
      const needBuildingId =
        item.buildingId ??
        (fallbackFloor?.buildingId ?? propFloor?.buildingId) ??
        (fallbackRoom?.buildingId ?? propRoom?.buildingId) ??
        null

      const haveBuilding =
        propBuildingDirect || propBuildingFromFloor || propBuildingFromRoom || fallbackBuilding

      if (needBuildingId && !haveBuilding) {
        setLoadingFields(s => ({ ...s, building: true }))
        try {
          const res = await apiDefinitions.getBuildingById(needBuildingId)
          if (res?.status === 200 && res.data?.data) setFallbackBuilding(res.data.data as BuildingDTO)
        } finally {
          setLoadingFields(s => ({ ...s, building: false }))
        }
      } else if (!needBuildingId) {
        setFallbackBuilding(null)
      }
    }

    run()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, item?.id])

  /** Final resolved entities */
  const resolvedRoom = propRoom ?? fallbackRoom ?? null
  const resolvedFloor = propFloor ?? fallbackFloor ?? null
  const resolvedBuilding = propBuildingDirect ?? propBuildingFromFloor ?? propBuildingFromRoom ?? fallbackBuilding ?? null

  const roomName = loadingFields.room ? 'Loading…' : (resolvedRoom?.name ?? 'N/A')
  const floorName = loadingFields.floor ? 'Loading…' : (resolvedFloor?.name ?? 'N/A')
  const buildingName = loadingFields.building ? 'Loading…' : (resolvedBuilding?.name ?? 'N/A')

  /** Service fields (handle both names) */
  const isServiceRequired = !!(item?.serviceRequired ?? item?.isServiceRequired ?? false)
  const scheduleType = (item?.scheduleType ?? '') as ScheduleType
  const startDate = item?.startDate ?? ''
  const endDate = item?.endDate ?? ''
  const intervalMonths = item?.intervalMonths ?? null
  const customDays = item?.customDays ?? null

  const scheduleTypeLabel = (t: ScheduleType) => {
    if (!t) return '—'
    switch (t) {
      case 'DAILY': return 'Daily'
      case 'WEEKLY': return 'Weekly'
      case 'MONTHLY': return 'Monthly'
      case 'YEARLY': return 'Yearly'
      case 'CUSTOM': return 'Custom (every N months)'
      default: return String(t)
    }
  }

  const isOpen = !!show
  const isLoadingWhole = isOpen && !item

  return (
    <div
      className={`modal fade ${isOpen ? 'show d-block' : ''}`}
      role="dialog"
      aria-modal={isOpen}
      style={{ background: isOpen ? 'rgba(0,0,0,0.5)' : 'none' }}
    >
      {/* Scoped styles */}
      <style>{`
        .vim-card { border-radius: 12px; box-shadow: 0 4px 20px rgba(0,0,0,.08); overflow: hidden; }
        .vim-header, .vim-footer { background: #f8f9fa; }
        .vim-label { font-size: .8rem; color: #6c757d; margin-bottom: .25rem; }
        .vim-value { font-weight: 600; color: #212529; padding: .6rem .75rem; border: 1px solid var(--bs-border-color); border-radius: .5rem; background: var(--bs-body-bg); }
        .badge-soft { background: #eef2ff; color: #3b5bdb; border: 1px solid #dbe4ff; }
        .badge-soft-success { background: #e6fcf5; color: #087f5b; border: 1px solid #c3fae8; }
        .badge-soft-secondary { background: #f1f3f5; color: #495057; border: 1px solid #e9ecef; }
        .badge-soft-primary { background: #e7f5ff; color: #1971c2; border: 1px solid #d0ebff; }
        .badge-soft-danger { background: #fff5f5; color: #c92a2a; border: 1px solid #ffe3e3; }
        .hr-muted { border-top: 1px dashed var(--bs-border-color); opacity: .9; }
      `}</style>

      <div className="modal-dialog modal-dialog-centered modal-lg">
        <div className="modal-content vim-card">
          <div className="modal-header vim-header border-bottom">
            <h5 className="modal-title d-flex align-items-center gap-2" style={{ color: '#333', fontSize: '1.05rem' }}>
              <i className="ti ti-package" /> View Item
            </h5>
            <button className="btn-close" onClick={onHide} aria-label="Close" />
          </div>

          <div className="modal-body" style={{ padding: '1.25rem' }}>
            {isLoadingWhole && (
              <div className="placeholder-glow">
                <div className="row g-3">
                  {Array.from({ length: 10 }).map((_, i) => (
                    <div className="col-12 col-md-6" key={i}>
                      <div className="placeholder col-6 mb-2" style={{ height: 14 }} />
                      <div className="placeholder col-12" style={{ height: 40 }} />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {!isLoadingWhole && item && (
              <>
                {/* Basic details */}
                <div className="row g-3">
                  <div className="col-12">
                    <div className="vim-label">Item Name</div>
                    <div className="vim-value">{item.itemName || '—'}</div>
                  </div>

                  <div className="col-12 col-md-6">
                    <div className="vim-label">Brand</div>
                    <div className="vim-value">{brandName}</div>
                  </div>

                  <div className="col-12 col-md-6">
                    <div className="vim-label">Item Type</div>
                    <div className="vim-value">{itemTypeName}</div>
                  </div>

                  <div className="col-12">
                    <div className="vim-label">Description</div>
                    <div className="vim-value">{item.description || '—'}</div>
                  </div>

                  <div className="col-12 col-md-6">
                    <div className="vim-label">Capacity</div>
                    <div className="vim-value">{item.capacity ?? 'N/A'}</div>
                  </div>

                  <div className="col-12 col-md-6">
                    <div className="vim-label">Serial Number</div>
                    <div className="vim-value">{item.serialNumber || '—'}</div>
                  </div>
                </div>

                <hr className="my-3 hr-muted" />

                {/* Lifecycle & Status */}
                <div className="row g-3">
                  <div className="col-12 col-md-6">
                    <div className="vim-label">Purchase Date</div>
                    <div className="vim-value">{formatDate(item.purchaseDate)}</div>
                  </div>
                  <div className="col-12 col-md-6">
                    <div className="vim-label">Using Since Date</div>
                    <div className="vim-value">{formatDate(item.usingSinceDate)}</div>
                  </div>
                  <div className="col-12 col-md-6">
                    <div className="vim-label">Warranty End Date</div>
                    <div className="vim-value">{formatDate(item.warrantyEndDate)}</div>
                  </div>
                  <div className="col-12 col-md-6">
                    <div className="vim-label">Status</div>
                    <div className="vim-value d-flex align-items-center gap-2">
                      <span className={`badge ${statusBadgeClass(item.status)}`}>
                        {item.status || '—'}
                      </span>
                    </div>
                  </div>
                  {item.comment && (
                    <div className="col-12">
                      <div className="vim-label">Comment</div>
                      <div className="vim-value">{item.comment}</div>
                    </div>
                  )}
                </div>

                <hr className="my-3 hr-muted" />

                {/* Location details */}
                <div className="row g-3">
                  <div className="col-12 col-md-6">
                    <div className="vim-label">Building</div>
                    <div className="vim-value">{loadingFields.building ? 'Loading…' : (resolvedBuilding?.name ?? 'N/A')}</div>
                  </div>

                  <div className="col-12 col-md-6">
                    <div className="vim-label">Floor</div>
                    <div className="vim-value">{loadingFields.floor ? 'Loading…' : (resolvedFloor?.name ?? 'N/A')}</div>
                  </div>

                  <div className="col-12 col-md-6">
                    <div className="vim-label">Room</div>
                    <div className="vim-value">{loadingFields.room ? 'Loading…' : (resolvedRoom?.name ?? 'N/A')}</div>
                  </div>
                </div>

                <hr className="my-3 hr-muted" />

                {/* Service / schedule */}
                <div className="row g-3">
                  <div className="col-12">
                    <div className="vim-label">Service Required</div>
                    <div className="vim-value d-flex align-items-center gap-2">
                      {isServiceRequired
                        ? <span className="badge badge-soft">Yes</span>
                        : <span className="badge badge-soft-danger">No</span>
                      }
                    </div>
                  </div>

                  {isServiceRequired && (
                    <>
                      <div className="col-12 col-md-6">
                        <div className="vim-label">Schedule Type</div>
                        <div className="vim-value">{scheduleTypeLabel(scheduleType)}</div>
                      </div>

                      <div className="col-12 col-md-6">
                        <div className="vim-label">Start Date</div>
                        <div className="vim-value">{item.startDate || '—'}</div>
                      </div>

                      <div className="col-12 col-md-6">
                        <div className="vim-label">End Date</div>
                        <div className="vim-value">{item.endDate || '—'}</div>
                      </div>

                      {scheduleType === 'CUSTOM' && (
                        <div className="col-12 col-md-6">
                          <div className="vim-label">Interval (months)</div>
                          <div className="vim-value">{intervalMonths ?? '—'}</div>
                        </div>
                      )}

                      {typeof customDays === 'number' && (
                        <div className="col-12 col-md-6">
                          <div className="vim-label">Custom Days</div>
                          <div className="vim-value">{customDays}</div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </>
            )}

            {!isLoadingWhole && !item && (
              <div className="alert alert-light border d-flex align-items-center mb-0" role="alert">
                <i className="ti ti-info-circle me-2"></i>
                Item not found or failed to load. Please try again.
              </div>
            )}
          </div>

          <div className="modal-footer vim-footer border-top">
            <Button variant="primary" onClick={onHide}>Close</Button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ViewItemModule
